/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Teach agents your business", description = "Give agents the business definitions, rules, and instructions they need to work with your data and complete tasks consistently.", lastUpdatedDate = new Date(1792454400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Teach agents your business",
        description: "Give agents the business definitions, rules, and instructions they need to work with your data and complete tasks consistently.",
        lastUpdatedDate: "Tue Oct 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Add business context",
          "depth": 0,
          "id": "add-business-context"
        }, {
          "value": "Create a file",
          "depth": 1,
          "id": "create-a-file"
        }, {
          "value": "Start from a template",
          "depth": 1,
          "id": "start-from-a-template"
        }, {
          "value": "Write clear instructions",
          "depth": 1,
          "id": "write-clear-instructions"
        }, {
          "value": "Choose where agents use context",
          "depth": 1,
          "id": "choose-where-agents-use-context"
        }, {
          "value": "Control when agents read files",
          "depth": 1,
          "id": "control-when-agents-read-files"
        }, {
          "value": "Create a skill",
          "depth": 0,
          "id": "create-a-skill"
        }, {
          "value": "Review and approve definitions",
          "depth": 0,
          "id": "review-and-approve-definitions"
        }, {
          "value": "Review agent assumptions",
          "depth": 1,
          "id": "review-agent-assumptions"
        }, {
          "value": "Review proposed updates",
          "depth": 1,
          "id": "review-proposed-updates"
        }, {
          "value": "Add a definition",
          "depth": 1,
          "id": "add-a-definition"
        }, {
          "value": "Choose when a definition applies",
          "depth": 1,
          "id": "choose-when-a-definition-applies"
        }, {
          "value": "See which definitions an agent used",
          "depth": 1,
          "id": "see-which-definitions-an-agent-used"
        }, {
          "value": "Run a context review",
          "depth": 0,
          "id": "run-a-context-review"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      h3: "h3",
      ol: "ol",
      li: "li",
      ul: "ul",
      pre: "pre",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["The ", _jsxs(_components.a, {
                href: "/workspace-management/roles#agents",
                children: [_jsx(_components.strong, {
                  children: "Configure Agents"
                }), " grant"]
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Business context helps agents understand your business, apply your rules, and use your data correctly."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Agents can use your data to answer questions and complete tasks, but your data doesn't always explain how your business works. For example, an agent might not know which customers count as active, how you calculate revenue, or which rules to follow when creating a campaign."
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge"
        }), " to give agents this information. Knowledge includes three types of context:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Use it to"
            }), _jsx(_components.th, {
              children: "Example"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Files"
              })
            }), _jsx(_components.td, {
              children: "Explain business rules, guidelines, and information agents need to complete tasks."
            }), _jsx(_components.td, {
              children: "Which data to use when calculating revenue."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Skills"
              })
            }), _jsx(_components.td, {
              children: "Give agents repeatable instructions for completing a task."
            }), _jsx(_components.td, {
              children: "How to prepare a weekly performance report."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Definitions"
              })
            }), _jsx(_components.td, {
              children: "Establish what a specific term or metric means."
            }), _jsx(_components.td, {
              children: "What qualifies someone as an active customer."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Agents can also create definitions based on assumptions they make during chats. You review and approve these definitions so agents use the same meaning in future conversations."
      }), "\n", _jsxs(_components.p, {
        children: ["You can manage knowledge directly or with help from the Context Agent. Open the Context Agent chat panel and select ", _jsx(_components.strong, {
          children: "Add new context"
        }), ", ", _jsx(_components.strong, {
          children: "Update context"
        }), ", or ", _jsx(_components.strong, {
          children: "Run a context review"
        }), ", or describe what you want to change. The Context Agent suggests changes for you to review and approve before they take effect."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-context-agent-panel.png",
          alt: "Context Agent chat panel open on the Brands page, with the Add new context, Update context, and Run a context review starters"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "add-business-context",
        children: "Add business context"
      }), "\n", _jsx(_components.p, {
        children: "Create a file to give agents business knowledge that isn't already in your data or schema. For example, you might explain how your company calculates a metric, which records to exclude from reports, or which data source to use when two sources disagree."
      }), "\n", _jsx(_components.p, {
        children: "Keep each file focused on one topic so agents read the instructions that apply to a task without loading unrelated information."
      }), "\n", _jsx(_components.h3, {
        id: "create-a-file",
        children: "Create a file"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Knowledge > Files"
          }), " and select ", _jsx(_components.strong, {
            children: "Create file"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose how to create the file:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Create with Context Agent"
              }), " — Describe what you need or provide an existing file. The agent drafts the content for you to review."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Start from a template"
              }), " — Use a template for a common type of business context."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Create from scratch"
              }), " — Write your own instructions."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Upload file"
              }), " — Import an existing file up to 50 MB. Markdown files open as editable documents."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Add or review the file's content. See ", _jsx(_components.a, {
            href: "#write-clear-instructions",
            children: "Write clear instructions"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose which agents and brands can use the file. See ", _jsx(_components.a, {
            href: "#choose-where-agents-use-context",
            children: "Choose where agents use context"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose when agents read the file. See ", _jsx(_components.a, {
            href: "#control-when-agents-read-files",
            children: "Control when agents read files"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Turn on ", _jsx(_components.strong, {
            children: "Published"
          }), " to make the file available to agents."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-knowledge-files.png",
          alt: "Knowledge Files list with the Create file menu open"
        })
      }), "\n", _jsx(_components.h3, {
        id: "start-from-a-template",
        children: "Start from a template"
      }), "\n", _jsx(_components.p, {
        children: "Templates give you a starting point for common types of business context. Choose the one that matches the information you want to share:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Template"
            }), _jsx(_components.th, {
              children: "Use it for"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Business context"
              })
            }), _jsx(_components.td, {
              children: "What your company does, how it makes money, your products, and your marketing goals"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Metric definitions & analysis guidelines"
              })
            }), _jsx(_components.td, {
              children: "Your key terms, how metrics are computed, which records to exclude, and lifecycle or campaign context"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience building guidelines"
              })
            }), _jsx(_components.td, {
              children: "Who's eligible to message, required consent and exclusions, and reusable building blocks"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Fiscal calendar & time"
              })
            }), _jsx(_components.td, {
              children: "Your fiscal calendar, time zone, and how to read relative dates"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Channels & attribution"
              })
            }), _jsx(_components.td, {
              children: "Where agents read campaign and attribution data, such as your warehouse versus the ad platforms"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "write-clear-instructions",
        children: "Write clear instructions"
      }), "\n", _jsx(_components.p, {
        children: "Write instructions agents can follow without making their own assumptions. When you explain how to calculate a metric, include which data to use, the calculation, any exclusions, and what to do when sources disagree. For example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "Use the marketing_conversions model for completed purchases.\nUse the Meta connection for spend, impressions, and clicks.\n\nCalculate ROAS as completed-purchase revenue divided by spend.\n\nIf the two sources disagree, show both values and explain\nthe difference.\n"
        })
      }), "\n", _jsx(_components.p, {
        children: "Be specific about what agents should do. Instead of telling an agent to \"use accurate revenue data,\" name the model and explain which records count."
      }), "\n", _jsx(_components.h3, {
        id: "choose-where-agents-use-context",
        children: "Choose where agents use context"
      }), "\n", _jsx(_components.p, {
        children: "Each file, skill, and definition has settings that determine which agents and brands use it."
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "Agents with access"
        }), ", select the products whose agents should use the context. You can select more than one."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Option"
            }), _jsx(_components.th, {
              children: "Agents that can use the context"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "All agents"
              }), " (default)"]
            }), _jsx(_components.td, {
              children: "Every agent in your workspace"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Ad Studio"
              })
            }), _jsx(_components.td, {
              children: "Agents that generate ads"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Lifecycle Studio"
              })
            }), _jsx(_components.td, {
              children: "Agents that create emails and campaigns"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Data"
              })
            }), _jsx(_components.td, {
              children: "Agents that build reports"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, if a file contains email footer requirements, select ", _jsx(_components.strong, {
          children: "Lifecycle Studio"
        }), " so agents in other products don't apply those rules. Chats in Agents can still use the context, even when you limit it to specific products."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-file-agents-with-access.png",
          alt: "Agents with access menu with Ad Studio and Lifecycle Studio selected and Data available"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "Brand profiles"
        }), ", select the brands the context applies to. For example, if your brands have different tone-of-voice guidelines, assign each file to its brand. Leave the setting on all brand profiles for rules that apply across your company."]
      }), "\n", _jsx(_components.h3, {
        id: "control-when-agents-read-files",
        children: "Control when agents read files"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Agent reads this file"
        }), " setting determines when an agent loads a file into a chat:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Option"
            }), _jsx(_components.th, {
              children: "When to use it"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "When relevant"
              })
            }), _jsx(_components.td, {
              children: "Most files. Agents read the file when it's relevant to the task."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Every chat (brand guidelines)"
              })
            }), _jsx(_components.td, {
              children: "Short brand guidelines that should apply to every chat."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Every chat (business context)"
              })
            }), _jsx(_components.td, {
              children: "Essential business information agents should always have."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["With ", _jsx(_components.strong, {
          children: "When relevant"
        }), " selected, agents use the file's ", _jsx(_components.strong, {
          children: "Usage guidance"
        }), " to decide when to read it. Hightouch generates this guidance from the file's content and updates it when the content changes."]
      }), "\n", _jsxs(_components.p, {
        children: ["Edit the guidance to make it more specific, such as \"Use when calculating paid-media efficiency, including ROAS, cost per acquisition, and campaign spend.\" If an agent reads a file when it shouldn't, or doesn't read it when you expect, check the ", _jsx(_components.strong, {
          children: "Usage guidance"
        }), " first."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-file-detail.png",
          alt: "File detail with Brand profiles, Agents with access, Agent reads this file set to Every chat (brand guidelines), and Usage guidance"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-a-skill",
        children: "Create a skill"
      }), "\n", _jsx(_components.p, {
        children: "Create a skill when you want agents to follow the same process each time they do a task, instead of explaining the steps in every chat. For example, you can create a skill that tells an agent how to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Prepare a weekly performance report."
        }), "\n", _jsx(_components.li, {
          children: "Photograph products in a consistent lifestyle setting."
        }), "\n", _jsx(_components.li, {
          children: "Review generated marketing copy before publishing."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-knowledge-skills-list.png",
          alt: "Skills list in Knowledge with scope, brand profiles, and agent scope for each skill"
        })
      }), "\n", _jsx(_components.p, {
        children: "To create a skill:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Knowledge > Skills"
          }), " and select ", _jsx(_components.strong, {
            children: "Create skill"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Give the skill a name that describes the task."
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Content"
          }), ", write the instructions as you would explain them to a new teammate. You can also select ", _jsx(_components.strong, {
            children: "Upload .md file"
          }), " to import existing instructions."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose which agents and brands can use the skill. See ", _jsx(_components.a, {
            href: "#choose-where-agents-use-context",
            children: "Choose where agents use context"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Turn on ", _jsx(_components.strong, {
            children: "Published"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["After you save the skill, Hightouch generates ", _jsx(_components.strong, {
          children: "Usage guidance"
        }), " that agents use to decide when to apply it. Edit the guidance if agents don't use the skill when you expect."]
      }), "\n", _jsxs(_components.p, {
        children: ["Skills marked ", _jsx(_components.strong, {
          children: "Managed by Hightouch"
        }), " are maintained by Hightouch. You can use them, but you can't edit them."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "review-and-approve-definitions",
        children: "Review and approve definitions"
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Definitions were previously called memories."
        })
      }), "\n", _jsx(_components.p, {
        children: "Definitions help agents use business terms and metrics consistently. As agents work, they make assumptions about your data. For example, an agent might assume that an active customer is someone who purchased in the last 30 days, or that a particular table holds your revenue data. Hightouch saves these assumptions as definitions so you can review and correct them."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-definitions-list.png",
          alt: "Definitions list in Knowledge with Assumed and Verified definitions, the Needs review filter, and Create definition"
        })
      }), "\n", _jsx(_components.p, {
        children: "Each definition has a status that shows whether it's been approved:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Assumed"
          }), " — An agent created the definition, and no one has approved it yet. Agents use it right away, but when your data contradicts it, they follow the data."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Verified"
          }), " — You approved the definition. Agents use it as written and can't change it directly."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Proposed update"
          }), " — An agent suggested a change to a Verified definition. Agents keep using the approved version until you apply the update."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "review-agent-assumptions",
        children: "Review agent assumptions"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Knowledge > Definitions"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Needs review"
          }), " to see assumptions agents have made and proposed updates to Verified definitions."]
        }), "\n", _jsx(_components.li, {
          children: "Open an assumption and review its content. Edit it if anything needs correcting."
        }), "\n", _jsx(_components.li, {
          children: "If the assumption has multiple versions, select the correct one under versions to review."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Approve"
          }), ", or ", _jsx(_components.strong, {
            children: "Save & approve"
          }), " if you made changes. If you selected a version, the button identifies it, such as ", _jsx(_components.strong, {
            children: "Approve version 2"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "When an agent updates an assumption you haven't approved, Hightouch saves the changes as a new version of the same definition rather than creating a duplicate."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-definition-assumption-versions.png",
          alt: "Assumed definition with two versions to review and the Approve version 2 button"
        })
      }), "\n", _jsx(_components.p, {
        children: "To reject an assumption, open its actions menu and delete the definition. Agents stop loading it."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-definition-assumed-approve.png",
          alt: "Assumed VIP customer definition opened from the Needs review filter, with the Approve button"
        })
      }), "\n", _jsx(_components.p, {
        children: "Review assumptions regularly. Agents use Assumed definitions before you approve them, so an incorrect assumption can affect later answers until you correct or delete it."
      }), "\n", _jsx(_components.h3, {
        id: "review-proposed-updates",
        children: "Review proposed updates"
      }), "\n", _jsxs(_components.p, {
        children: ["Agents can't change Verified definitions directly. Instead, they propose updates for you to review. When an agent proposes a change, the definition appears under ", _jsx(_components.strong, {
          children: "Needs review"
        }), " with the status ", _jsx(_components.strong, {
          children: "Proposed update"
        }), ". Agents keep using the approved definition until you apply a proposal."]
      }), "\n", _jsx(_components.p, {
        children: "To review an update:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Knowledge > Definitions"
          }), " and select ", _jsx(_components.strong, {
            children: "Needs review"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open a definition with the status ", _jsx(_components.strong, {
            children: "Proposed update"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Proposed updates"
          }), ", select a proposal. Turn on ", _jsx(_components.strong, {
            children: "Show changes"
          }), " to compare it with the current definition."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose what to do:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Apply the proposal"
              }), " — Select the apply button, such as ", _jsx(_components.strong, {
                children: "Apply proposal 1"
              }), ", to replace the current definition. Applying one proposal discards any other proposals for that definition."]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Keep the current definition"
              }), " — Select ", _jsx(_components.strong, {
                children: "Dismiss all"
              }), " and confirm to discard every proposed update."]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Dismissing proposals can't be undone."
        })
      }), "\n", _jsx(_components.h3, {
        id: "add-a-definition",
        children: "Add a definition"
      }), "\n", _jsx(_components.p, {
        children: "Create a definition yourself when you already know how your team defines a term, instead of waiting for an agent to assume it. For example, if your team counts an active customer as someone who purchased within the last 30 days, add that definition so agents use it."
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge > Definitions"
        }), " and select ", _jsx(_components.strong, {
          children: "Create definition"
        }), ". Definitions you create start as ", _jsx(_components.strong, {
          children: "Verified"
        }), ", so agents use them without further approval."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-when-a-definition-applies",
        children: "Choose when a definition applies"
      }), "\n", _jsx(_components.p, {
        children: "Some terms mean different things depending on the data. For example, an active customer might mean a purchase in the last 30 days for one parent model, and a login in the last 7 days for another."
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Scope"
        }), " to control when agents load a definition:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Workspace"
          }), " — In every chat."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Scoped to parent models or sources"
          }), " — Only in chats that query the parent models or sources you select."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also choose which agents and brands use the definition, and add ", _jsx(_components.strong, {
          children: "Usage guidance"
        }), " that explains when it applies. See ", _jsx(_components.a, {
          href: "#choose-where-agents-use-context",
          children: "Choose where agents use context"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-definition-detail.png",
          alt: "Definition detail scoped to a parent model, with Brand profiles, Agents with access, and Usage guidance"
        })
      }), "\n", _jsx(_components.h3, {
        id: "see-which-definitions-an-agent-used",
        children: "See which definitions an agent used"
      }), "\n", _jsxs(_components.p, {
        children: ["Check which definitions an agent used before you act on an answer. In an ", _jsx(_components.a, {
          href: "/agents/chats#check-what-the-agent-used",
          children: "Agents chat"
        }), ", answers that used definitions show indicators that count them by status:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Beside a paragraph or list item"
          }), " — The definitions used for that part of the answer."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "At the end of the answer"
          }), " — Every definition used in the answer."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Hover over an indicator to see the definitions, or select it to open them in the ", _jsx(_components.strong, {
          children: "Session context"
        }), " panel."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-chat-inline-citation.png",
          alt: "Agent answer with a pill beside a paragraph, hovered to show the Verified definition behind it"
        })
      }), "\n", _jsx(_components.p, {
        children: "To review every definition used in a chat:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Show context"
          }), " in the chat header."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Session context"
          }), " panel, find the definitions the agent used under ", _jsx(_components.strong, {
            children: "Definitions"
          }), ". The panel also lists the data and files the agent used."]
        }), "\n", _jsx(_components.li, {
          children: "Select a definition to read it."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "View in Context Hub"
          }), " to approve or edit it."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If a definition shows ", _jsx(_components.strong, {
          children: "Updated since the agent used it"
        }), ", the agent used an earlier version when it answered."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "run-a-context-review",
        children: "Run a context review"
      }), "\n", _jsx(_components.p, {
        children: "As your business changes, the context you've given agents can go out of date or contradict itself. For example, you might change how you calculate revenue but leave the older calculation in another file, or two definitions might describe the same metric differently."
      }), "\n", _jsx(_components.p, {
        children: "Run a context review to have the Context Agent check your files, definitions, styles, and brand profiles for outdated or conflicting information. When two sources disagree, it asks you which one is correct before it suggests a change. Run a review when you change your metrics, data models, attribution rules, or business processes."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Open the Context Agent chat panel."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Run a context review"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Review the ", _jsx(_components.strong, {
            children: "Suggested changes"
          }), ". Each suggestion is marked as new, an update, or a deletion."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Review"
          }), " on a suggestion to see what it would change."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Approve"
          }), " or ", _jsx(_components.strong, {
            children: "Reject"
          }), " for each suggestion. To act on every suggestion at once, select ", _jsx(_components.strong, {
            children: "Approve all"
          }), " or ", _jsx(_components.strong, {
            children: "Reject all"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-context-agent-review.png",
          alt: "Context Agent new chat with the Run a context review starter highlighted"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-context-review-suggested-changes.png",
          alt: "Suggested changes card with four updates, each with a Review button, and Reject all and Approve all"
        })
      }), "\n", _jsx(_components.p, {
        children: "Changes take effect only after you approve them. You can keep chatting with the Context Agent to refine a suggestion before you approve it."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/external",
            children: "Sync external context"
          }), " to bring metric and dimension definitions from Snowflake or Databricks into Knowledge instead of writing them as files."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/guardrails#create-agent-reviewers",
            children: "Set guardrails"
          }), " to turn a review skill into an agent reviewer for ad creative."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
